import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { requestsApi, specialtiesApi, requestTypeSettingsApi, studiesApi } from '../lib/api';
import { BrandLoader } from '../components/BrandLoader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, FileText, FlaskConical, FileCheck, X, Link2, Search, Users, CheckCircle2, Download, Info, Calendar, Upload, Loader2, Plus, Lock, ShieldCheck } from 'lucide-react';
import { SpecialtyMultiSelect } from '../components/SpecialtyMultiSelect';

// Default form configuration
const DEFAULT_FORM_CONFIG = {
  title: { enabled: true, required: true, label: 'Study Title', placeholder: 'Enter the study title', help_text: null },
  description: { enabled: true, required: true, label: 'Description', placeholder: 'Provide a brief description of the study', help_text: null },
  specialties: { enabled: true, required: true, label: 'Specialties', placeholder: null, help_text: 'Select one or more specialties' },
  disease_condition: { enabled: true, required: false, label: 'Disease/Condition', placeholder: 'e.g., Type 2 Diabetes, Breast Cancer', help_text: null },
  protocol_summary: { enabled: true, required: false, label: 'Protocol Summary', placeholder: 'Brief summary of the protocol (optional)', help_text: null },
  linked_fea: { enabled: true, required: false, label: 'Link to Previous Feasibility Study', placeholder: null, help_text: 'Link this CTA to a completed FEA to improve your CTA request workflow' }
};

export default function NewRequest() {
  const { isCompanyRep, isCoordinator } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const studyId = searchParams.get('study_id');
  const presetType = searchParams.get('type');
  const [loading, setLoading] = useState(false);
  const [specialties, setSpecialties] = useState([]);
  const [companyFeaRequests, setCompanyFeaRequests] = useState([]);
  const [feaRefInput, setFeaRefInput] = useState('');
  const [validatingFea, setValidatingFea] = useState(false);
  const [linkedFeaInfo, setLinkedFeaInfo] = useState(null);
  const [formConfig, setFormConfig] = useState(DEFAULT_FORM_CONFIG);
  const [formConfigLoading, setFormConfigLoading] = useState(true);
  const [specialtySearch, setSpecialtySearch] = useState('');
  const [customFields, setCustomFields] = useState([]);
  const [unifiedFieldOrder, setUnifiedFieldOrder] = useState(null);
  const [uploadingFiles, setUploadingFiles] = useState({});  // Track file upload progress per field
  const [selectedFiles, setSelectedFiles] = useState({});    // Track selected files for file_upload fields
  const [myStudies, setMyStudies] = useState([]);
  const [studyPickerLoading, setStudyPickerLoading] = useState(!studyId);
  const [studyInfo, setStudyInfo] = useState(null);
  const [autoLinkedFea, setAutoLinkedFea] = useState(null);
  // FT (Financial Transaction) — coordinator flow
  const [ftStep, setFtStep] = useState('choice'); // 'choice' | 'pick-study' | 'create-study' | 'form'
  const [ftStudies, setFtStudies] = useState([]);
  const [ftStudiesLoading, setFtStudiesLoading] = useState(false);
  const [ftInvestigators, setFtInvestigators] = useState([]);
  const [selectedFtStudy, setSelectedFtStudy] = useState(null); // The picked or created study
  const [selectedFtLinkedCta, setSelectedFtLinkedCta] = useState(null); // Optional CTA within the study
  const [selectedInvestigators, setSelectedInvestigators] = useState([]); // [{user_id, is_co_investigator}]
  const [investigatorSearch, setInvestigatorSearch] = useState('');
  // iter167 Issue 35 — Admin-configurable per-study cap (fetched once at
  // mount). Same shape as StudyWorkspace.
  const [perStudyLimits, setPerStudyLimits] = useState({
    FEA: { mode: 'unlimited', max_count: 1 },
    CTA: { mode: 'unlimited', max_count: 1 },
  });
  const [formData, setFormData] = useState({
    type: presetType || 'FEA',
    title: '',
    description: '',
    specialty_ids: [],
    disease_condition: '',
    protocol_summary: '',
    linked_fea_request_id: null,
    linked_request_id: null, // FT: parent CTA id
    custom_field_values: {},
    study_id: studyId || null
  });

  useEffect(() => {
    // Company Reps create FEA/CTA. Coordinators create FT only.
    if (!isCompanyRep() && !isCoordinator()) {
      navigate('/dashboard');
      return;
    }
    fetchMasterData();
    // Coordinators default to FT; Company Reps default to FEA (or presetType)
    const isCoordinatorOnly = isCoordinator() && !isCompanyRep();
    const defaultType = isCoordinatorOnly ? 'FT' : (presetType || 'FEA');
    if (formData.type !== defaultType) {
      setFormData(prev => ({ ...prev, type: defaultType }));
    }
    fetchFormConfig(presetType || defaultType);
    
    // Coordinator (FT) → company-style study picker (auto-pick when deep-linked from Create Study)
    if (isCoordinatorOnly) {
      fetchFtPickerData(studyId);
      return;
    }
    
    if (!studyId) {
      fetchMyStudies();
    } else {
      // Fetch study details — pre-populate specialties and auto-link FEA→CTA
      studiesApi.get(studyId).then(res => {
        const study = res.data;
        setStudyInfo(study);
        if (study?.specialty_ids?.length > 0) {
          setFormData(prev => ({ ...prev, specialty_ids: study.specialty_ids }));
        }
        // Auto-link: if creating CTA and study has a completed FEA
        const currentType = presetType || 'FEA';
        if (currentType === 'CTA' && study?.requests) {
          const completedFea = study.requests.find(r => 
            r.type === 'FEA' && ['COMPLETED', 'SIGNED', 'IN_PROGRESS', 'ROUTED_TO_CONSULTANTS'].includes(r.status)
          );
          if (completedFea) {
            setAutoLinkedFea(completedFea);
            setLinkedFeaInfo({
              id: completedFea.id,
              ref_no: completedFea.ref_no,
              title: completedFea.title,
              assignments: completedFea.assignments || []
            });
            setFormData(prev => ({ ...prev, linked_fea_request_id: completedFea.id }));
          }
        }
      }).catch(() => {});
    }
  }, []);
  
  const fetchFtPickerData = async (autopickStudyId = null) => {
    setFtStudiesLoading(true);
    try {
      const [studiesRes, invRes] = await Promise.all([
        requestsApi.getCoordinatorFtStudyPicker(),
        requestsApi.getCoordinatorFtInvestigators(),
      ]);
      setFtStudies(studiesRes.data || []);
      setFtInvestigators(invRes.data || []);
      // Deep link from the Create Study wizard (?study_id=X&type=FT) → auto-pick that study
      if (autopickStudyId) {
        const target = (studiesRes.data || []).find(s => s.id === autopickStudyId);
        if (target) {
          handlePickStudy(target);
        } else {
          try {
            const sRes = await studiesApi.get(autopickStudyId);
            handlePickStudy(sRes.data);
          } catch (e) {
            console.error('Failed to auto-pick study:', e);
          }
        }
      }
    } catch (error) {
      console.error('Failed to load FT picker data:', error);
      toast.error('Failed to load FT picker data');
    } finally {
      setFtStudiesLoading(false);
    }
  };
  
  const [ftPathMode, setFtPathMode] = useState(null); // 'A' = link to existing, 'B' = new study (legacy)
  const [manualCta, setManualCta] = useState({ ref_no: '', title: '' }); // Path B only
  const [ftStudyContext, setFtStudyContext] = useState(null); // {study, ctas, feas, auto_linked, inherited_investigators}
  const [loadingStudyContext, setLoadingStudyContext] = useState(false);
  
  const handlePickStudy = async (study) => {
    setSelectedFtStudy(study);
    setSelectedFtLinkedCta(null);
    setSelectedInvestigators([]);
    setFtPathMode('A');
    setManualCta({ ref_no: '', title: '' });
    setFormData(prev => ({
      ...prev,
      linked_request_id: null,
      study_id: study.id,
      specialty_ids: study.specialty_ids || [],
      title: `FT for ${study.title}`,
    }));
    setFtStep('form');
    // Fetch study context: linked CTA/FEA + inherited investigators
    setLoadingStudyContext(true);
    try {
      const res = await requestsApi.getCoordinatorFtStudyContext(study.id);
      const ctx = res.data;
      setFtStudyContext(ctx);
      // Edge case: study with no CTAs → fall back to manual CTA fields (Path B style)
      // while keeping the FT attached to the chosen study.
      setFtPathMode((ctx.ctas && ctx.ctas.length > 0) ? 'A' : 'B');
      // Auto-link: if exactly one CTA, use it. If multiple, user must pick.
      if (ctx.auto_linked) {
        setSelectedFtLinkedCta({ id: ctx.auto_linked.request_id, ref_no: ctx.auto_linked.ref_no, title: ctx.auto_linked.title, _kind: ctx.auto_linked.type });
        if (ctx.auto_linked.type === 'CTA') {
          setFormData(prev => ({ ...prev, linked_request_id: ctx.auto_linked.request_id }));
        }
      }
      // Auto-inherit investigators (read-only)
      if (ctx.inherited_investigators?.length > 0) {
        setSelectedInvestigators(ctx.inherited_investigators.map(i => ({ user_id: i.user_id, is_co_investigator: !!i.is_co_investigator })));
      }
    } catch (e) {
      console.error('Failed to load study context:', e);
      toast.error('Failed to load study context');
    } finally {
      setLoadingStudyContext(false);
    }
  };
  
  const toggleInvestigator = (userId, isCoI) => {
    setSelectedInvestigators(prev => {
      const exists = prev.find(p => p.user_id === userId);
      if (exists) {
        return prev.filter(p => p.user_id !== userId);
      }
      return [...prev, { user_id: userId, is_co_investigator: isCoI }];
    });
  };
  
  const toggleInvestigatorRole = (userId) => {
    setSelectedInvestigators(prev => prev.map(p =>
      p.user_id === userId ? { ...p, is_co_investigator: !p.is_co_investigator } : p
    ));
  };

  // Fetch form config when type changes (non-FT). FT is handled by the path-aware effect below.
  useEffect(() => {
    if (formData.type !== 'FT') {
      fetchFormConfig(formData.type);
      fetchCustomFields(formData.type);
    }
    if (formData.type === 'CTA') {
      fetchCompanyFeaRequests();
    }
  }, [formData.type]);

  // When the Coordinator picks/creates a study OR toggles path, refetch FT form-config and custom-fields
  // path-aware (FT_PATH_A vs FT_PATH_B).
  useEffect(() => {
    if (!isCoordinatorOnly) return;
    if (ftStep !== 'form') return;
    if (formData.type !== 'FT') return;
    const path = ftPathMode || 'A';
    fetchFormConfig('FT', path);
    fetchCustomFields('FT', path);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ftStep, ftPathMode, formData.type]);

  const fetchFormConfig = async (type, path) => {
    setFormConfigLoading(true);
    try {
      const res = await requestTypeSettingsApi.getFormConfig(type, path);
      setFormConfig({ ...DEFAULT_FORM_CONFIG, ...res.data });
      setUnifiedFieldOrder(res.data?.unified_field_order || null);
    } catch (error) {
      console.error('Failed to fetch form config:', error);
      setFormConfig(DEFAULT_FORM_CONFIG);
      setUnifiedFieldOrder(null);
    } finally {
      setFormConfigLoading(false);
    }
  };

  const fetchCustomFields = async (type, path) => {
    try {
      const res = await requestTypeSettingsApi.getCustomFields(type, path);
      const fields = res.data?.fields || [];
      setCustomFields(fields);
      // Initialize custom field values in formData
      const customValues = {};
      fields.forEach(field => {
        if (field.enabled !== false) {
          customValues[field.id] = field.type === 'checkbox' ? false : (field.type === 'multiselect' ? [] : '');
        }
      });
      setFormData(prev => ({ ...prev, custom_field_values: customValues }));
    } catch (error) {
      console.error('Failed to fetch custom fields:', error);
      setCustomFields([]);
    }
  };

  const fetchMasterData = async () => {
    try {
      const [specsRes, feaPslRes, ctaPslRes] = await Promise.all([
        specialtiesApi.getAll(),
        requestTypeSettingsApi.getPerStudyLimit('FEA').catch(() => ({ data: null })),
        requestTypeSettingsApi.getPerStudyLimit('CTA').catch(() => ({ data: null })),
      ]);
      setSpecialties(specsRes.data);
      const norm = (r) => {
        const d = r?.data || {};
        const mode = (d.mode === 'once' || d.mode === 'fixed') ? d.mode : 'unlimited';
        const max_count = Math.max(1, parseInt(d.max_count, 10) || 1);
        return { mode, max_count };
      };
      setPerStudyLimits({ FEA: norm(feaPslRes), CTA: norm(ctaPslRes) });
    } catch (error) {
      console.error('Failed to fetch master data:', error);
    }
  };

  const fetchMyStudies = async () => {
    try {
      const res = await studiesApi.getAll();
      // iter167 — Include COMPLETED studies too, so the Company Rep can
      // add follow-up FEA / CTA requests to a study that already has a
      // closed request lineage. Previously COMPLETED studies were filtered
      // out, leading to the "missing study" bug in the picker.
      setMyStudies(
        res.data.filter(
          s => s.status === 'ACTIVE' || s.status === 'ON_HOLD' || s.status === 'COMPLETED'
        )
      );
    } catch (error) {
      console.error('Failed to fetch studies:', error);
    } finally {
      setStudyPickerLoading(false);
    }
  };

  const handleSelectStudy = (selectedStudyId) => {
    const study = myStudies.find(s => s.id === selectedStudyId);
    // iter167 Issue 35 — Consider a type "taken" only if it's actually at
    // the admin-configured cap (not just "exists once"). Terminal-state
    // requests do not count.
    const TERMINAL_STATUSES_LOCAL = new Set(['REJECTED', 'WITHDRAWN', 'FAILED', 'CLOSED']);
    const isTypeCapped = (t) => {
      const cfg = perStudyLimits[t] || { mode: 'unlimited', max_count: 1 };
      if (cfg.mode === 'unlimited') return false;
      const cap = cfg.mode === 'once' ? 1 : Math.max(1, cfg.max_count);
      const activeCount = (study?.requests_summary || [])
        .filter(r => r.type === t && !TERMINAL_STATUSES_LOCAL.has(r.status))
        .length;
      return activeCount >= cap;
    };
    const existingTypes = ['FEA', 'CTA'].filter(isTypeCapped);
    // iter166a — Determine the resulting request type. If the user picked
    // the picker without first changing the type and the current type is
    // already taken, fall back to the other available type.
    let nextType = formData.type;
    if (existingTypes.includes(formData.type)) {
      const otherType = formData.type === 'FEA' ? 'CTA' : 'FEA';
      if (!existingTypes.includes(otherType)) {
        nextType = otherType;
      }
    }
    // iter166a — Persist studyInfo + carry over study specialties so
    // downstream UI (Linked-from-Study banner, specialty defaults) works the
    // same way as the deep-link flow (`?study_id=…`).
    setStudyInfo(study || null);
    setFormData(prev => ({
      ...prev,
      type: nextType,
      study_id: selectedStudyId,
      specialty_ids: (study?.specialty_ids?.length ? study.specialty_ids : prev.specialty_ids),
    }));

    // iter166a — When the resulting type is CTA and the study has an
    // existing FEA we can reuse, auto-link it so the form shows the
    // "Linked from Study" banner — matching the deep-link flow that this
    // picker-driven flow was missing.
    if (nextType === 'CTA' && study?.requests_summary) {
      const linkableFea = study.requests_summary.find(r =>
        r.type === 'FEA' &&
        ['COMPLETED', 'SIGNED', 'IN_PROGRESS', 'ROUTED_TO_CONSULTANTS'].includes(r.status)
      );
      if (linkableFea) {
        setAutoLinkedFea(linkableFea);
        setLinkedFeaInfo({
          id: linkableFea.id,
          ref_no: linkableFea.ref_no,
          title: linkableFea.title,
        });
        setFormData(prev => ({ ...prev, linked_fea_request_id: linkableFea.id }));
      } else {
        setAutoLinkedFea(null);
        setLinkedFeaInfo(null);
        setFormData(prev => ({ ...prev, linked_fea_request_id: null }));
      }
    } else {
      setAutoLinkedFea(null);
      setLinkedFeaInfo(null);
      setFormData(prev => ({ ...prev, linked_fea_request_id: null }));
    }
  };

  const fetchCompanyFeaRequests = async () => {
    try {
      const res = await requestsApi.getCompanyFeaRequests();
      setCompanyFeaRequests(res.data);
    } catch (error) {
      console.error('Failed to fetch FEA requests:', error);
    }
  };

  const handleValidateFeaRef = async () => {
    if (!feaRefInput.trim()) return;
    
    setValidatingFea(true);
    try {
      const res = await requestsApi.validateFeaRef(feaRefInput.trim());
      setLinkedFeaInfo(res.data);
      setFormData(prev => ({ ...prev, linked_fea_request_id: res.data.id }));
      toast.success('FEA request found and linked');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'FEA request not found');
      setLinkedFeaInfo(null);
    } finally {
      setValidatingFea(false);
    }
  };

  const handleSelectFea = (feaId) => {
    if (feaId === 'none') {
      setLinkedFeaInfo(null);
      setFormData(prev => ({ ...prev, linked_fea_request_id: null }));
      setFeaRefInput('');
    } else {
      const fea = companyFeaRequests.find(f => f.id === feaId);
      if (fea) {
        setLinkedFeaInfo(fea);
        setFormData(prev => ({ ...prev, linked_fea_request_id: fea.id }));
        setFeaRefInput(fea.ref_no);
      }
    }
  };

  const clearLinkedFea = () => {
    setLinkedFeaInfo(null);
    setFormData(prev => ({ ...prev, linked_fea_request_id: null }));
    setFeaRefInput('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate based on form config
    if (formConfig.title?.required && !formData.title.trim()) {
      toast.error(`${formConfig.title?.label || 'Title'} is required`);
      return;
    }
    if (formConfig.description?.required && !formData.description.trim()) {
      toast.error(`${formConfig.description?.label || 'Description'} is required`);
      return;
    }
    // Specialties are optional for FT (form does not surface a specialties picker;
    // specialties are inherited from the Study and Admin/Chairman/CRD/Director/ExecDir
    // can add them later via the Study/Request details page).
    if (formData.type !== 'FT' && formConfig.specialties?.required && (!formData.specialty_ids || formData.specialty_ids.length === 0)) {
      toast.error(`Please select at least one ${formConfig.specialties?.label || 'specialty'}`);
      return;
    }
    if (formConfig.disease_condition?.required && formConfig.disease_condition?.enabled !== false && !formData.disease_condition?.trim()) {
      toast.error(`${formConfig.disease_condition?.label || 'Disease/Condition'} is required`);
      return;
    }
    if (formConfig.protocol_summary?.required && formConfig.protocol_summary?.enabled !== false && !formData.protocol_summary?.trim()) {
      toast.error(`${formConfig.protocol_summary?.label || 'Protocol Summary'} is required`);
      return;
    }
    if (formConfig.linked_fea?.required && formConfig.linked_fea?.enabled !== false && formData.type === 'CTA' && !formData.linked_fea_request_id) {
      toast.error(`${formConfig.linked_fea?.label || 'FEA Link'} is required`);
      return;
    }
    
    // Validate required custom fields
    for (const field of customFields.filter(f => f.enabled !== false && f.required)) {
      if (!checkFieldConditions(field)) continue; // Skip if conditions not met
      
      const value = formData.custom_field_values?.[field.id];
      
      // Special handling for file_upload fields
      if (field.type === 'file_upload') {
        if (!selectedFiles[field.id]) {
          toast.error(`${field.label} is required - please select a file`);
          return;
        }
        continue;
      }
      
      const isEmpty = value === null || value === undefined || value === '' || 
                      (Array.isArray(value) && value.length === 0);
      
      if (isEmpty) {
        toast.error(`${field.label} is required`);
        return;
      }
    }
    
    // Check file_download fields with require_download
    for (const field of customFields.filter(f => f.type === 'file_download' && f.require_download)) {
      const value = formData.custom_field_values?.[field.id];
      if (!value?.downloaded) {
        toast.error(`Please download ${field.label} before submitting`);
        return;
      }
    }
    
    setLoading(true);

    try {
      const payload = { ...formData };
      // For FT (Coordinator): include picked investigators
      if (isCoordinatorOnly && payload.type === 'FT') {
        payload.ft_investigators = selectedInvestigators;
        // Path B: store the manually-entered CTA reference + title
        if (ftPathMode === 'B' && (manualCta.ref_no.trim() || manualCta.title.trim())) {
          payload.manual_cta_ref_no = manualCta.ref_no.trim() || null;
          payload.manual_cta_title = manualCta.title.trim() || null;
        }
      }
      // iter151 — Cross-study duplicate-title soft warning. Backend returns 409
      // with code='duplicate_request_title' when an active request with the
      // same (case-insensitive) title already exists for this company in a
      // different study. We surface a confirm() and retry with ?confirm_duplicate_title=true.
      let response;
      try {
        response = await requestsApi.create(payload);
      } catch (firstErr) {
        const detail = firstErr?.response?.data?.detail;
        if (firstErr?.response?.status === 409 && detail && typeof detail === 'object' && detail.code === 'duplicate_request_title') {
          const proceed = window.confirm(`${detail.message}\n\nClick OK to create a duplicate anyway, or Cancel to change the title.`);
          if (!proceed) {
            setLoading(false);
            return;
          }
          response = await requestsApi.create(payload, { params: { confirm_duplicate_title: true } });
        } else {
          throw firstErr;
        }
      }
      const requestId = response.data.id;
      
      // Upload any files from file_upload custom fields
      const fileUploadFields = customFields.filter(f => f.type === 'file_upload' && f.enabled !== false);
      for (const field of fileUploadFields) {
        const file = selectedFiles[field.id];
        if (file) {
          try {
            const uploadFormData = new FormData();
            uploadFormData.append('file', file);
            uploadFormData.append('doc_type', field.label);  // Use field label as doc type
            uploadFormData.append('custom_field_id', field.id);  // Track which custom field this came from
            
            await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/requests/${requestId}/documents`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              },
              body: uploadFormData
            });
          } catch (uploadError) {
            console.error(`Failed to upload file for ${field.label}:`, uploadError);
            // Continue even if file upload fails
          }
        }
      }
      
      toast.success('Request created successfully');
      // iter167 — Always land on the new Request Details page after creation
      // (both the wizard flow AND the Study-Workspace "Add Request" entry).
      // Previously, when a `studyId` was present (wizard or workspace), we
      // bounced back to the Study workspace which forced the user to find
      // the just-created request manually before they could submit it.
      navigate(`/requests/${requestId}`);
    } catch (error) {
      // iter167 Issue 35 — Surface the per-study instance-cap 409 with a
      // clear, friendly message. The backend returns detail as an object
      // with `code: 'per_study_limit_reached'` and a human `message` field.
      const detail = error?.response?.data?.detail;
      if (error?.response?.status === 409 && detail && typeof detail === 'object' && detail.code === 'per_study_limit_reached') {
        toast.error(detail.message || 'This study has reached its request-type limit.');
      } else if (detail && typeof detail === 'object') {
        toast.error(detail.message || 'Failed to create request');
      } else {
        toast.error(detail || 'Failed to create request');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleSpecialty = (specialtyId) => {
    setFormData(prev => {
      const ids = prev.specialty_ids || [];
      if (ids.includes(specialtyId)) {
        return { ...prev, specialty_ids: ids.filter(id => id !== specialtyId) };
      } else {
        return { ...prev, specialty_ids: [...ids, specialtyId] };
      }
    });
  };

  // Helper to get field config value with fallback
  const getFieldLabel = (field) => formConfig[field]?.label || DEFAULT_FORM_CONFIG[field]?.label || field;
  const getFieldPlaceholder = (field) => formConfig[field]?.placeholder || DEFAULT_FORM_CONFIG[field]?.placeholder || '';
  const getFieldHelpText = (field) => formConfig[field]?.help_text || DEFAULT_FORM_CONFIG[field]?.help_text || '';
  const isFieldRequired = (field) => formConfig[field]?.required === true;
  const isFieldEnabled = (field) => {
    const fc = formConfig[field];
    if (!fc) return true;
    if (fc._deleted === true) return false;
    return fc.enabled !== false;
  };

  // Filter specialties based on search
  const filteredSpecialties = specialties.filter(spec => 
    spec.name.toLowerCase().includes(specialtySearch.toLowerCase()) ||
    spec.code.toLowerCase().includes(specialtySearch.toLowerCase())
  );

  // Handle custom field value change
  const handleCustomFieldChange = (fieldId, value) => {
    setFormData(prev => ({
      ...prev,
      custom_field_values: {
        ...prev.custom_field_values,
        [fieldId]: value
      }
    }));
  };

  // Check if a field's conditions are met
  const checkFieldConditions = (field) => {
    if (!field.conditions || field.conditions.length === 0) return true;
    
    return field.conditions.every(condition => {
      const dependentValue = formData.custom_field_values?.[condition.field_id];
      
      switch (condition.operator) {
        case 'equals':
          return dependentValue === condition.value || 
                 (typeof dependentValue === 'boolean' && String(dependentValue) === condition.value);
        case 'not_equals':
          return dependentValue !== condition.value;
        case 'is_empty':
          return !dependentValue || dependentValue === '' || 
                 (Array.isArray(dependentValue) && dependentValue.length === 0);
        case 'is_not_empty':
          return dependentValue && dependentValue !== '' && 
                 !(Array.isArray(dependentValue) && dependentValue.length === 0);
        case 'contains':
          return dependentValue && String(dependentValue).toLowerCase().includes(String(condition.value).toLowerCase());
        default:
          return true;
      }
    });
  };

  // Render a single custom field
  const renderCustomField = (field) => {
    if (!field.enabled) return null;
    if (!checkFieldConditions(field)) return null;
    
    const value = formData.custom_field_values?.[field.id];
    
    switch (field.type) {
      case 'text':
        return (
          <div key={field.id} className="space-y-2">
            <Label>{field.label} {field.required && '*'}</Label>
            <Input
              value={value || ''}
              onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
              placeholder={field.placeholder}
              required={field.required}
              data-testid={`custom-field-${field.id}`}
            />
            {field.help_text && <p className="text-xs text-slate-500">{field.help_text}</p>}
          </div>
        );
      
      case 'textarea':
        return (
          <div key={field.id} className="space-y-2">
            <Label>{field.label} {field.required && '*'}</Label>
            <Textarea
              value={value || ''}
              onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
              placeholder={field.placeholder}
              rows={4}
              required={field.required}
              data-testid={`custom-field-${field.id}`}
            />
            {field.help_text && <p className="text-xs text-slate-500">{field.help_text}</p>}
          </div>
        );
      
      case 'select':
        return (
          <div key={field.id} className="space-y-2">
            <Label>{field.label} {field.required && '*'}</Label>
            <Select 
              value={value || ''} 
              onValueChange={(val) => handleCustomFieldChange(field.id, val)}
            >
              <SelectTrigger data-testid={`custom-field-${field.id}`}>
                <SelectValue placeholder={`Select ${field.label}...`} />
              </SelectTrigger>
              <SelectContent>
                {field.options?.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {field.help_text && <p className="text-xs text-slate-500">{field.help_text}</p>}
          </div>
        );
      
      case 'multiselect':
        return (
          <div key={field.id} className="space-y-2">
            <Label>{field.label} {field.required && '*'}</Label>
            <div className="border rounded-md p-3 bg-slate-50">
              {(value || []).length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3 pb-3 border-b">
                  {(value || []).map(val => {
                    const opt = field.options?.find(o => o.value === val);
                    return opt ? (
                      <Badge key={val} variant="secondary" className="flex items-center gap-1 bg-blue-100 text-blue-800">
                        {opt.label}
                        <X 
                          className="w-3 h-3 cursor-pointer hover:text-red-500" 
                          onClick={() => handleCustomFieldChange(field.id, (value || []).filter(v => v !== val))}
                        />
                      </Badge>
                    ) : null;
                  })}
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                {field.options?.map(opt => (
                  <div key={opt.value} className="flex items-center gap-2">
                    <Checkbox
                      checked={(value || []).includes(opt.value)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          handleCustomFieldChange(field.id, [...(value || []), opt.value]);
                        } else {
                          handleCustomFieldChange(field.id, (value || []).filter(v => v !== opt.value));
                        }
                      }}
                    />
                    <Label className="font-normal cursor-pointer text-sm">{opt.label}</Label>
                  </div>
                ))}
              </div>
            </div>
            {field.help_text && <p className="text-xs text-slate-500">{field.help_text}</p>}
          </div>
        );
      
      case 'date':
        return (
          <div key={field.id} className="space-y-2">
            <Label>{field.label} {field.required && '*'}</Label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="date"
                value={value || ''}
                onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
                className="pl-10"
                required={field.required}
                data-testid={`custom-field-${field.id}`}
              />
            </div>
            {field.help_text && <p className="text-xs text-slate-500">{field.help_text}</p>}
          </div>
        );
      
      case 'checkbox':
        return (
          <div key={field.id} className="flex items-center gap-3 p-3 border rounded-lg bg-slate-50">
            <Checkbox
              checked={value || false}
              onCheckedChange={(checked) => handleCustomFieldChange(field.id, checked)}
              data-testid={`custom-field-${field.id}`}
            />
            <div>
              <Label className="font-medium cursor-pointer">{field.label}</Label>
              {field.help_text && <p className="text-xs text-slate-500">{field.help_text}</p>}
            </div>
          </div>
        );
      
      case 'message':
        return (
          <div key={field.id} className="p-4 border-l-4 border-blue-500 bg-blue-50 rounded-r-lg">
            <div className="flex items-start gap-2">
              <Info className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="font-medium text-blue-800">{field.label}</p>
                <p className="text-sm text-blue-700 mt-1">{field.message_content || field.help_text}</p>
              </div>
            </div>
          </div>
        );
      
      case 'file_upload':
        return (
          <div key={field.id} className="space-y-2">
            <Label>{field.label} {field.required && '*'}</Label>
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 bg-slate-50 hover:border-blue-300 transition-colors">
              {selectedFiles[field.id] ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="font-medium text-slate-900">{selectedFiles[field.id].name}</p>
                      <p className="text-xs text-slate-500">
                        {(selectedFiles[field.id].size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                    <Badge className="bg-emerald-100 text-emerald-700">Ready to upload</Badge>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedFiles(prev => {
                        const newFiles = { ...prev };
                        delete newFiles[field.id];
                        return newFiles;
                      });
                      handleCustomFieldChange(field.id, null);
                    }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="text-center">
                  <input
                    type="file"
                    id={`file-upload-${field.id}`}
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setSelectedFiles(prev => ({ ...prev, [field.id]: file }));
                        // Store file reference in form data - will be uploaded on submit
                        handleCustomFieldChange(field.id, { fileName: file.name, fileSize: file.size });
                      }
                    }}
                    data-testid={`custom-field-${field.id}`}
                  />
                  <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => document.getElementById(`file-upload-${field.id}`).click()}
                  >
                    <Upload className="w-4 h-4 mr-1" />
                    Select File
                  </Button>
                  <p className="text-xs text-slate-500 mt-2">
                    This file will appear in the Documents tab after submission
                  </p>
                </div>
              )}
            </div>
            {field.help_text && <p className="text-xs text-slate-500">{field.help_text}</p>}
          </div>
        );
      
      case 'file_download':
        // Don't show file_download fields that don't have a template uploaded
        if (!field.template_file_id) {
          return null;  // Hide completely - admin hasn't uploaded the template yet
        }
        return (
          <div key={field.id} className="p-4 border border-amber-200 bg-amber-50 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Download className="w-5 h-5 text-amber-600" />
                <div>
                  <p className="font-medium text-amber-800">{field.label}</p>
                  {field.template_file_name && (
                    <p className="text-sm text-amber-600">{field.template_file_name}</p>
                  )}
                  {field.help_text && <p className="text-xs text-amber-700 mt-1">{field.help_text}</p>}
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    const response = await fetch(
                      `${process.env.REACT_APP_BACKEND_URL}/api/template-files/${field.template_file_id}/download`,
                      {
                        headers: {
                          'Authorization': `Bearer ${localStorage.getItem('token')}`
                        }
                      }
                    );
                    if (!response.ok) throw new Error('Download failed');
                    
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = field.template_file_name || 'template';
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(a);
                    
                    handleCustomFieldChange(field.id, { downloaded: true });
                    toast.success('Template downloaded');
                  } catch (error) {
                    toast.error('Failed to download template');
                  }
                }}
                className="border-amber-300 text-amber-700 hover:bg-amber-100"
              >
                <Download className="w-4 h-4 mr-1" />
                Download
              </Button>
            </div>
            {field.require_download && (
              <p className="text-xs text-amber-600 mt-2">
                * Required to download before submitting
              </p>
            )}
          </div>
        );
      
      default:
        return null;
    }
  };

  // Show loading skeleton while form config is loading
  if (formConfigLoading && !formConfig) {
    return (
      <DashboardLayout title="New Request">
        <div className="max-w-3xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-10 bg-slate-200 rounded w-1/4"></div>
            <div className="h-64 bg-slate-200 rounded"></div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Standard field renderers
  const renderStandardField = (fieldKey) => {
    if (!isFieldEnabled(fieldKey)) return null;
    
    switch (fieldKey) {
      case 'linked_fea':
        if (formData.type !== 'CTA') return null;
        
        // Auto-linked from study — locked, no manual override
        if (autoLinkedFea) {
          const feaInvestigators = (autoLinkedFea.assignments || []).filter(a => a.assignment_role === 'INVESTIGATOR');
          return (
            <div key="linked_fea" className="space-y-3">
              {/* Auto-linkage banner */}
              <div className="p-4 rounded-lg border-2 border-emerald-200 bg-emerald-50/50" data-testid="auto-linked-fea-banner">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  <span className="font-semibold text-emerald-800">Linked from Study</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-white rounded-md border border-emerald-200">
                  <div>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-teal-100 text-teal-700 text-xs">FEA</Badge>
                      <span className="font-mono text-xs text-slate-500">{autoLinkedFea.ref_no}</span>
                      <Badge className="bg-emerald-100 text-emerald-700 text-xs">{autoLinkedFea.status?.replace(/_/g, ' ')}</Badge>
                    </div>
                    <p className="font-medium text-slate-900 mt-1">{autoLinkedFea.title}</p>
                  </div>
                </div>
                {feaInvestigators.length > 0 && (
                  <div className="mt-3 p-3 bg-white rounded-md border border-emerald-200">
                    <p className="text-xs font-medium text-emerald-700 mb-1.5">Inherited from FEA:</p>
                    <div className="flex flex-wrap gap-1.5">
                      {feaInvestigators.map((a, i) => (
                        <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-xs text-emerald-700">
                          <Users className="w-3 h-3" />
                          {a.assignment_role === 'INVESTIGATOR' ? 'PI' : a.assignment_role}: {a.assignee_name}
                          {a.status === 'DONE' && <CheckCircle2 className="w-3 h-3 text-emerald-500" />}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <p className="text-xs text-emerald-600 mt-2">
                  This CTA is automatically linked to the FEA in your study. The review team will be carried over.
                </p>
              </div>
            </div>
          );
        }
        
        // No auto-linkage: CTAs can be created without an FEA link. Manual FEA selector
        // has been removed per product decision (May 2026) — keep the field a no-op.
        return null;
      
      case 'title':
        return (
          <div key="title" className="space-y-2">
            <Label htmlFor="title">
              {getFieldLabel('title')} {isFieldRequired('title') && '*'}
            </Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder={getFieldPlaceholder('title')}
              required={isFieldRequired('title')}
              data-testid="title-input"
            />
            {getFieldHelpText('title') && (
              <p className="text-xs text-slate-500">{getFieldHelpText('title')}</p>
            )}
          </div>
        );
      
      case 'description':
        return (
          <div key="description" className="space-y-2">
            <Label htmlFor="description">
              {getFieldLabel('description')} {isFieldRequired('description') && '*'}
            </Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder={getFieldPlaceholder('description')}
              rows={4}
              required={isFieldRequired('description')}
              data-testid="description-input"
            />
            {getFieldHelpText('description') && (
              <p className="text-xs text-slate-500">{getFieldHelpText('description')}</p>
            )}
          </div>
        );
      
      case 'specialties':
        // FT: specialties are inherited from the linked CTA — show read-only badges
        if (formData.type === 'FT') {
          if (!specialties.length || !formData.specialty_ids?.length) return null;
          const inherited = specialties.filter(s => formData.specialty_ids.includes(s.id));
          return (
            <div key="specialties" className="space-y-2">
              <Label>Specialties <span className="text-slate-400 text-sm ml-1">(inherited from Study)</span></Label>
              <div className="flex flex-wrap gap-1.5 p-3 rounded-md bg-slate-50 border border-slate-200">
                {inherited.map(s => (
                  <Badge key={s.id} className="bg-emerald-100 text-emerald-700 text-xs">{s.name}</Badge>
                ))}
              </div>
            </div>
          );
        }
        return (
          <div key="specialties" className="space-y-2">
            <Label>
              {getFieldLabel('specialties')} {isFieldRequired('specialties') && '*'} 
              <span className="text-slate-400 text-sm ml-1">(select one or more)</span>
            </Label>
            {getFieldHelpText('specialties') && (
              <p className="text-xs text-slate-500 mb-1">{getFieldHelpText('specialties')}</p>
            )}
            <SpecialtyMultiSelect
              specialties={specialties}
              value={formData.specialty_ids || []}
              onChange={(ids) => setFormData((prev) => ({ ...prev, specialty_ids: ids }))}
              testidPrefix="request-spec"
            />
            {isFieldRequired('specialties') && formData.specialty_ids?.length === 0 && (
              <p className="text-xs text-amber-600">Please select at least one specialty</p>
            )}
          </div>
        );
      
      case 'disease_condition':
        if (formData.type !== 'FEA') return null;
        return (
          <div key="disease_condition" className="space-y-2">
            <Label htmlFor="disease">
              {getFieldLabel('disease_condition')} {isFieldRequired('disease_condition') && '*'}
            </Label>
            <Input
              id="disease"
              value={formData.disease_condition}
              onChange={(e) => handleChange('disease_condition', e.target.value)}
              placeholder={getFieldPlaceholder('disease_condition')}
              required={isFieldRequired('disease_condition')}
              data-testid="disease-input"
            />
            {getFieldHelpText('disease_condition') && (
              <p className="text-xs text-slate-500">{getFieldHelpText('disease_condition')}</p>
            )}
          </div>
        );
      
      case 'protocol_summary':
        return (
          <div key="protocol_summary" className="space-y-2">
            <Label htmlFor="protocol">
              {getFieldLabel('protocol_summary')} {isFieldRequired('protocol_summary') && '*'}
            </Label>
            <Textarea
              id="protocol"
              value={formData.protocol_summary}
              onChange={(e) => handleChange('protocol_summary', e.target.value)}
              placeholder={getFieldPlaceholder('protocol_summary')}
              rows={4}
              required={isFieldRequired('protocol_summary')}
              data-testid="protocol-input"
            />
            {getFieldHelpText('protocol_summary') && (
              <p className="text-xs text-slate-500">{getFieldHelpText('protocol_summary')}</p>
            )}
          </div>
        );
      
      default:
        return null;
    }
  };

  // Get ordered field list for the form
  // Render FT-specific inline sections (Linked CTA / Investigators / Manual CTA) by fieldKey
  const renderFtSection = (fieldId) => {
    if (!selectedFtStudy) return null;
    if (!isFieldEnabled(fieldId)) return null;
    
    if (fieldId === 'cta_display' && ftPathMode === 'A') {
      if (loadingStudyContext) {
        return <div key="cta_display" className="flex items-center gap-2 text-xs text-slate-500"><Loader2 className="w-3 h-3 animate-spin" /> Loading study context...</div>;
      }
      if (!ftStudyContext) return null;
      return (
        <div key="cta_display" data-testid="ft-linked-request-readonly">
          <Label className="text-sm text-slate-700 mb-1 block">{formConfig.cta_display?.label || 'Linked CTA'}</Label>
          {ftStudyContext.ctas.length > 1 ? (
            <Select
              value={selectedFtLinkedCta?.id || ''}
              onValueChange={(v) => {
                const c = ftStudyContext.ctas.find(x => x.id === v);
                setSelectedFtLinkedCta({ id: c.id, ref_no: c.ref_no, title: c.title, _kind: 'CTA' });
                setFormData(prev => ({ ...prev, linked_request_id: c.id }));
              }}
            >
              <SelectTrigger data-testid="ft-linked-cta-select"><SelectValue placeholder="Pick a CTA..." /></SelectTrigger>
              <SelectContent>
                {ftStudyContext.ctas.map(c => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.ref_no} — {c.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : selectedFtLinkedCta && selectedFtLinkedCta._kind === 'CTA' ? (
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-white border border-emerald-200">
              <Badge className="text-[10px] bg-violet-100 text-violet-700">CTA</Badge>
              <span className="font-mono text-xs text-slate-500">{selectedFtLinkedCta.ref_no}</span>
              <span className="text-xs text-slate-800 font-medium">{selectedFtLinkedCta.title}</span>
              <Lock className="w-3 h-3 text-slate-400" />
            </div>
          ) : (
            <p className="text-xs text-slate-500 italic">No CTA found in this study</p>
          )}
        </div>
      );
    }
    
    if (fieldId === 'manual_cta_ref_no' && ftPathMode === 'B') {
      return (
        <div key="manual_cta_ref_no" className="space-y-1">
          <Label className="text-sm text-slate-700">
            {formConfig.manual_cta_ref_no?.label || 'CTA Number'}
            {formConfig.manual_cta_ref_no?.required && <span className="text-red-500 ml-1">*</span>}
            <span className="text-slate-400 ml-1 text-xs">(manually entered)</span>
          </Label>
          <Input value={manualCta.ref_no} onChange={(e) => setManualCta(prev => ({ ...prev, ref_no: e.target.value }))} placeholder={formConfig.manual_cta_ref_no?.placeholder || 'e.g., CTA-2024-007'} data-testid="ft-manual-cta-ref" />
        </div>
      );
    }
    
    if (fieldId === 'manual_cta_title' && ftPathMode === 'B') {
      return (
        <div key="manual_cta_title" className="space-y-1">
          <Label className="text-sm text-slate-700">
            {formConfig.manual_cta_title?.label || 'CTA Name'}
            {formConfig.manual_cta_title?.required && <span className="text-red-500 ml-1">*</span>}
            <span className="text-slate-400 ml-1 text-xs">(manually entered)</span>
          </Label>
          <Input value={manualCta.title} onChange={(e) => setManualCta(prev => ({ ...prev, title: e.target.value }))} placeholder={formConfig.manual_cta_title?.placeholder || 'e.g., Drug X Phase III Agreement'} data-testid="ft-manual-cta-title" />
        </div>
      );
    }
    
    if (fieldId === 'investigators') {
      if (loadingStudyContext || !ftStudyContext) return null;
      // Path A with inherited investigators → read-only
      if (ftPathMode === 'A' && ftStudyContext.inherited_investigators?.length > 0) {
        return (
          <div key="investigators" className="space-y-2" data-testid="ft-inherited-investigators">
            <Label>{formConfig.investigators?.label || 'Investigators'} <span className="text-slate-400 text-sm ml-1">(inherited from {ftStudyContext.auto_linked?.type || 'parent'})</span></Label>
            <div className="flex flex-wrap gap-1.5 p-3 rounded-md bg-slate-50 border border-slate-200">
              {ftStudyContext.inherited_investigators.map((i, idx) => (
                <span key={idx} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-xs text-emerald-700">
                  <Users className="w-3 h-3" />
                  {i.is_co_investigator ? 'Co-I' : 'PI'}: {i.user_name || '—'}
                </span>
              ))}
            </div>
          </div>
        );
      }
      // Otherwise editable picker
      return (
        <div key="investigators" className="space-y-2" data-testid="ft-investigator-picker">
          <Label>
            {formConfig.investigators?.label || 'Investigators (PI + Co-Investigators)'}
            {formConfig.investigators?.required && <span className="text-red-500 ml-1">*</span>}
          </Label>
          {ftPathMode === 'A' && (
            <p className="text-xs text-slate-500">No CTA or FEA found in this study to inherit from. Pick the Principal Investigator and any Co-Investigators directly.</p>
          )}
          <Input placeholder="Search investigators by name or email..." value={investigatorSearch} onChange={(e) => setInvestigatorSearch(e.target.value)} className="mb-2" data-testid="ft-investigator-search" />
          <div className="max-h-56 overflow-y-auto border border-slate-200 rounded-md divide-y">
            {ftInvestigators.filter(u => {
              if (!investigatorSearch.trim()) return true;
              const q = investigatorSearch.toLowerCase();
              return (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
            }).map(u => {
              const selected = selectedInvestigators.find(p => p.user_id === u.id);
              return (
                <div key={u.id} className="flex items-center justify-between gap-3 p-2 hover:bg-slate-50">
                  <div className="flex items-center gap-2 min-w-0">
                    <Checkbox checked={!!selected} onCheckedChange={() => toggleInvestigator(u.id, false)} data-testid={`ft-investigator-${u.id}`} />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{u.name}</p>
                      <p className="text-xs text-slate-500 truncate">{u.email}</p>
                    </div>
                  </div>
                  {selected && (
                    <button type="button" onClick={() => toggleInvestigatorRole(u.id)} className={`text-[10px] px-2 py-1 rounded-full border ${selected.is_co_investigator ? 'bg-amber-50 border-amber-200 text-amber-700' : 'bg-emerald-50 border-emerald-200 text-emerald-700'}`} data-testid={`ft-investigator-role-toggle-${u.id}`}>
                      {selected.is_co_investigator ? 'Co-I' : 'PI'}
                    </button>
                  )}
                </div>
              );
            })}
            {ftInvestigators.length === 0 && (<div className="p-4 text-center text-xs text-slate-500">No active investigators on the platform.</div>)}
          </div>
        </div>
      );
    }
    
    return null;
  };

  const getOrderedFormFields = () => {
    // FT has its own field set (different from FEA/CTA): no specialties/disease_condition/linked_fea
    const isFt = formData.type === 'FT';
    const ftSectionKeys = ['cta_display', 'manual_cta_ref_no', 'manual_cta_title', 'investigators'];
    const standardFieldKeys = isFt
      ? ['title', 'description', 'protocol_summary', ...ftSectionKeys]
      : ['linked_fea', 'title', 'description', 'specialties', 'disease_condition', 'protocol_summary'];
    const enabledCustomFields = customFields.filter(f => f.enabled !== false);

    // Single dispatcher: routes FT structural keys to renderFtSection, plain fields to renderStandardField
    const renderFieldById = (fieldId) => {
      if (isFt && ftSectionKeys.includes(fieldId)) {
        return renderFtSection(fieldId);
      }
      if (standardFieldKeys.includes(fieldId)) {
        return renderStandardField(fieldId);
      }
      const customField = enabledCustomFields.find(f => f.id === fieldId);
      if (customField) return renderCustomField(customField);
      return null;
    };
    
    if (unifiedFieldOrder && unifiedFieldOrder.length > 0) {
      const renderedFields = [];
      const renderedIds = new Set();
      
      for (const fieldId of unifiedFieldOrder) {
        if (renderedIds.has(fieldId)) continue;
        renderedIds.add(fieldId);
        const rendered = renderFieldById(fieldId);
        if (rendered) renderedFields.push(rendered);
      }
      
      // Render any fields not in the saved order (safety fallback)
      for (const key of standardFieldKeys) {
        if (!renderedIds.has(key)) {
          const rendered = renderFieldById(key);
          if (rendered) renderedFields.push(rendered);
        }
      }
      for (const cf of enabledCustomFields) {
        if (!renderedIds.has(cf.id)) {
          renderedFields.push(renderCustomField(cf));
        }
      }
      
      return renderedFields;
    }
    
    // Default order: standard fields (incl. FT structural keys), then custom fields
    const renderedFields = [];
    for (const key of standardFieldKeys) {
      const rendered = renderFieldById(key);
      if (rendered) renderedFields.push(rendered);
    }
    for (const cf of enabledCustomFields) {
      renderedFields.push(renderCustomField(cf));
    }
    return renderedFields;
  };

  // Coordinator (FT) — study picker matching the Company "Add Request to Study" experience
  const isCoordinatorOnly = isCoordinator() && !isCompanyRep();
  if (isCoordinatorOnly && ftStep !== 'form') {
    if (ftStudiesLoading) {
      return (
        <DashboardLayout title="Add Request">
          <div className="max-w-3xl mx-auto py-12 flex items-center justify-center" data-testid="ft-picker-loading">
            <BrandLoader size="md" label="Loading studies…" />
          </div>
        </DashboardLayout>
      );
    }
    const ftSearchQ = investigatorSearch.trim().toLowerCase();
    const visibleFtStudies = ftStudies.filter(s =>
      !ftSearchQ || (s.title || '').toLowerCase().includes(ftSearchQ) || (s.company_name || '').toLowerCase().includes(ftSearchQ)
    );
    return (
      <DashboardLayout title="Add Request">
        <div className="max-w-3xl mx-auto" data-testid="new-request-page">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-6" data-testid="ft-back-btn">
            <ArrowLeft size={16} className="mr-2" /> Back to Dashboard
          </Button>

          <div className="mb-6">
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Add Request to Study</h1>
            <p className="text-slate-500 mt-1">Select an existing study or create a new one</p>
          </div>

          {/* Create New Study card — navigates to the 2-step Create Study wizard */}
          <Card
            className="mb-4 border-dashed border-2 border-blue-200 bg-blue-50/30 hover:bg-blue-50 cursor-pointer transition-colors"
            onClick={() => navigate('/studies/new')}
            data-testid="ft-create-study-card"
          >
            <CardContent className="p-5 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <Plus className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-blue-900">Create New Study</h3>
                <p className="text-sm text-blue-600">For legacy work not yet on the platform — create a quick study, then attach the FT.</p>
              </div>
            </CardContent>
          </Card>

          {ftStudies.length > 0 ? (
            <>
              <div className="flex items-center gap-3 my-4">
                <div className="h-px flex-1 bg-slate-200" />
                <span className="text-xs text-slate-400 font-medium">OR ADD TO EXISTING STUDY</span>
                <div className="h-px flex-1 bg-slate-200" />
              </div>

              <Input
                placeholder="Search studies by title or company..."
                value={investigatorSearch}
                onChange={(e) => setInvestigatorSearch(e.target.value)}
                className="mb-3"
                data-testid="ft-study-search"
              />

              <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
                {visibleFtStudies.map(s => (
                  <Card
                    key={s.id}
                    className="transition-all hover:border-slate-300 hover:shadow-sm cursor-pointer"
                    onClick={() => handlePickStudy(s)}
                    data-testid={`study-picker-${s.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold text-slate-900">{s.title}</h4>
                            <Badge className={
                              ((s.status || 'ACTIVE') === 'ACTIVE')
                                ? 'bg-emerald-100 text-emerald-700 text-[10px]'
                                : (s.status === 'COMPLETED'
                                    ? 'bg-blue-100 text-blue-700 text-[10px]'
                                    : 'bg-slate-100 text-slate-500 text-[10px]')
                            }>{s.status || 'ACTIVE'}</Badge>
                          </div>
                          {s.company_name && <p className="text-sm text-slate-500 line-clamp-1 mb-2">{s.company_name}</p>}
                          <div className="flex items-center gap-2 flex-wrap">
                            {s.request_counts?.FEA > 0 && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] bg-white">
                                <span className="w-1.5 h-1.5 rounded-full bg-teal-500" />
                                <span className="font-medium">{s.request_counts.FEA > 1 ? `${s.request_counts.FEA} ` : ''}FEA</span>
                              </span>
                            )}
                            {s.request_counts?.CTA > 0 && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] bg-white">
                                <span className="w-1.5 h-1.5 rounded-full bg-violet-500" />
                                <span className="font-medium">{s.request_counts.CTA > 1 ? `${s.request_counts.CTA} ` : ''}CTA</span>
                              </span>
                            )}
                            {s.request_counts?.FT > 0 && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] bg-white">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                <span className="font-medium">{s.request_counts.FT > 1 ? `${s.request_counts.FT} ` : ''}FT</span>
                              </span>
                            )}
                            <span className="text-[10px] text-blue-500">+ Add FT</span>
                          </div>
                        </div>
                        {s.specialty_names?.length > 0 && (
                          <div className="flex flex-wrap gap-1 ml-3">
                            {s.specialty_names.slice(0, 4).map((name, i) => (
                              <Badge key={i} variant="outline" className="text-[10px] px-1.5 py-0">{name}</Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {visibleFtStudies.length === 0 && (
                  <p className="text-sm text-slate-400 text-center py-6">No studies match your search.</p>
                )}
              </div>
            </>
          ) : (
            <p className="text-sm text-slate-400 text-center mt-6">No studies on the platform yet — create one above.</p>
          )}
        </div>
      </DashboardLayout>
    );
  }


  // If no study_id, show study picker (or a loading placeholder while studies are being fetched).
  // Without this short-circuit, the main form briefly flashes before the picker takes over.
  if (!formData.study_id && !isCoordinatorOnly) {
    if (studyPickerLoading) {
      return (
        <DashboardLayout title="Add Request">
          <div className="max-w-3xl mx-auto py-12 flex items-center justify-center" data-testid="study-picker-loading">
            <BrandLoader size="md" label="Loading studies…" />
          </div>
        </DashboardLayout>
      );
    }
    return (
      <DashboardLayout title="Add Request">
        <div className="max-w-3xl mx-auto" data-testid="new-request-page">
          <Button variant="ghost" onClick={() => navigate('/studies')} className="mb-6">
            <ArrowLeft size={16} className="mr-2" /> Back to Studies
          </Button>

          <div className="mb-6">
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Add Request to Study</h1>
            <p className="text-slate-500 mt-1">Select an existing study or create a new one</p>
          </div>

          {/* Create New Study card */}
          <Card className="mb-4 border-dashed border-2 border-blue-200 bg-blue-50/30 hover:bg-blue-50 cursor-pointer transition-colors" onClick={() => navigate('/studies/new')}>
            <CardContent className="p-5 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <Plus className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-blue-900">Create New Study</h3>
                <p className="text-sm text-blue-600">Start a new research study from scratch</p>
              </div>
            </CardContent>
          </Card>

          {myStudies.length > 0 && (
            <>
              <div className="flex items-center gap-3 my-4">
                <div className="h-px flex-1 bg-slate-200" />
                <span className="text-xs text-slate-400 font-medium">OR ADD TO EXISTING STUDY</span>
                <div className="h-px flex-1 bg-slate-200" />
              </div>

              <div className="space-y-2">
                {myStudies.map(s => {
                  // FAILED requests don't block; company can retry.
                  const existingTypes = (s.requests_summary || []).filter(r => r.status !== 'FAILED').map(r => r.type);
                  const allTypesTaken = existingTypes.includes('FEA') && existingTypes.includes('CTA');
                  return (
                    <Card
                      key={s.id}
                      className={`transition-all ${
                        allTypesTaken
                          ? 'opacity-50 cursor-not-allowed'
                          : 'hover:border-slate-300 hover:shadow-sm cursor-pointer'
                      }`}
                      onClick={() => !allTypesTaken && handleSelectStudy(s.id)}
                      data-testid={`study-picker-${s.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold text-slate-900">{s.title}</h4>
                              <Badge className={s.status === 'ACTIVE' ? 'bg-emerald-100 text-emerald-700 text-[10px]' : 'bg-slate-100 text-slate-500 text-[10px]'}>{s.status}</Badge>
                            </div>
                            {s.description && <p className="text-sm text-slate-500 line-clamp-1 mb-2">{s.description}</p>}
                            <div className="flex items-center gap-2 flex-wrap">
                              {(s.requests_summary || []).map(r => (
                                <span key={r.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] bg-white">
                                  <span className={`w-1.5 h-1.5 rounded-full ${r.type === 'FEA' ? 'bg-teal-500' : 'bg-violet-500'}`} />
                                  <span className="font-medium">{r.type}</span>
                                </span>
                              ))}
                              {allTypesTaken && (
                                <span className="text-[10px] text-slate-400 italic">All types created</span>
                              )}
                              {!allTypesTaken && (
                                <span className="text-[10px] text-blue-500">
                                  + Add {existingTypes.includes('FEA') ? 'CTA' : existingTypes.includes('CTA') ? 'FEA' : 'FEA or CTA'}
                                </span>
                              )}
                            </div>
                          </div>
                          {s.specialty_names?.length > 0 && (
                            <div className="flex flex-wrap gap-1 ml-3">
                              {s.specialty_names.map((name, i) => (
                                <Badge key={i} variant="outline" className="text-[10px] px-1.5 py-0">{name}</Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </DashboardLayout>
    );
  }

  const selectedStudy = myStudies.find(s => s.id === formData.study_id) || studyInfo;
  // iter167 Issue 35 — Report which request types are AT CAP for the
  // selected study, rather than a hardcoded "already exists" rule. This
  // matches the backend enforcement in POST /requests and honours the
  // admin's per-study limit config (unlimited / once / fixed).
  const TERMINAL_STATUSES_NR = new Set(['REJECTED', 'WITHDRAWN', 'FAILED', 'CLOSED']);
  const _typeCapReached = (type) => {
    if (!selectedStudy) return false;
    const cfg = perStudyLimits[type] || { mode: 'unlimited', max_count: 1 };
    if (cfg.mode === 'unlimited') return false;
    const cap = cfg.mode === 'once' ? 1 : Math.max(1, cfg.max_count);
    const rows = selectedStudy.requests_summary
      || (selectedStudy.requests || []).map(r => ({ type: r.type, status: r.status }));
    const activeCount = rows.filter(r => r.type === type && !TERMINAL_STATUSES_NR.has(r.status)).length;
    return activeCount >= cap;
  };
  // Keep the old variable name so downstream JSX ("Already created" badges
  // and disabled radio state) needs minimal edits — but now the semantics
  // are "types the wizard should block", which encompasses both the old
  // once-per-study rule AND the new admin-configurable fixed cap.
  const studyExistingTypes = selectedStudy
    ? ['FEA', 'CTA'].filter(t => _typeCapReached(t))
    : [];

  return (
    <DashboardLayout title="New Request">
      <div className="max-w-3xl mx-auto" data-testid="new-request-page">
        <Button 
          variant="ghost" 
          onClick={() => {
            if (isCoordinatorOnly) {
              setSelectedFtStudy(null);
              setSelectedFtLinkedCta(null);
              setSelectedInvestigators([]);
              setFtPathMode(null);
              setManualCta({ ref_no: '', title: '' });
              setFormData(prev => ({ ...prev, linked_request_id: null, study_id: null, title: '' }));
              setFtStep('choice');
              return;
            }
            formData.study_id ? navigate(`/studies/${formData.study_id}`) : navigate('/studies');
          }}
          className="mb-6"
          data-testid="new-request-back-btn"
        >
          <ArrowLeft size={16} className="mr-2" />
          {isCoordinatorOnly ? 'Back' : (formData.study_id ? 'Back to Study' : 'Back to Studies')}
        </Button>

        <Card className="shadow-md border-slate-200">
          <CardHeader className="bg-gradient-to-br from-slate-50 to-white border-b border-slate-200 rounded-t-lg">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <CardTitle className="font-['Manrope'] text-2xl tracking-tight">
                  {isCoordinatorOnly ? 'New Financial Transaction' : 'Create New Request'}
                </CardTitle>
                <CardDescription className="mt-1.5 text-sm">
                  {isCoordinatorOnly
                    ? 'Fill in the Financial Transaction details. PI and Co-Investigators can be edited below.'
                    : (studyInfo?.title
                        // iter166g — subtitle now reads "<Study title> · Study Workspace"
                        // when we already know the parent study, instead of the
                        // generic "Submit a new Feasibility Study or CTA request".
                        ? <span><span className="font-semibold text-slate-700">{studyInfo.title}</span> · Study Workspace</span>
                        : 'Submit a new Feasibility Study or CTA request')}
                </CardDescription>
              </div>
              {studyInfo?.title && !isCoordinatorOnly && (
                <Badge className="shrink-0 bg-blue-100 text-blue-700 border border-blue-200 hover:bg-blue-100">
                  Study Workspace
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Linked Study banner (FT) — auto-link CTA/FEA + inherited investigators */}
              {isCoordinatorOnly && selectedFtStudy && (
                <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50/50 p-4" data-testid="ft-linked-study-banner">
                  <div className="flex items-center gap-2 mb-2">
                    <Link2 className="w-4 h-4 text-emerald-600" />
                    <span className="font-semibold text-emerald-800 text-sm">Attached to Study</span>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-sm font-medium text-slate-900">{selectedFtStudy.title}</span>
                  </div>
                  {/* Read-only inherited Company */}
                  <p className="text-xs text-slate-600 mt-1" data-testid="ft-linked-study-company">
                    Company: <span className="font-medium text-slate-800">{selectedFtStudy.company_name || ftStudyContext?.study?.company_name || <span className="text-amber-600">— (no Company assigned)</span>}</span>
                  </p>
                </div>
              )}

              {/* CTA section + Investigator section are now rendered inline via getOrderedFormFields()
                  using the unified_field_order from AdminConfig. See renderFtSection(). */}

              {/* Request Type (hidden for FT-only flow) */}
              {!isCoordinatorOnly && (
              <div className="space-y-3">
                <Label>Request Type</Label>
                <RadioGroup
                  value={formData.type}
                  onValueChange={(value) => handleChange('type', value)}
                  className="grid grid-cols-3 gap-4"
                >
                  {isCompanyRep() && (
                  <div className="relative">
                    <RadioGroupItem value="FEA" id="fea" className="peer sr-only" disabled={studyExistingTypes.includes('FEA')} />
                    <Label
                      htmlFor="fea"
                      className={`flex flex-col items-center justify-center rounded-lg border-2 p-6 transition-colors ${
                        studyExistingTypes.includes('FEA')
                          ? 'border-slate-200 bg-slate-50 opacity-50 cursor-not-allowed'
                          : 'border-slate-200 bg-white cursor-pointer hover:bg-slate-50 peer-data-[state=checked]:border-teal-500 peer-data-[state=checked]:bg-teal-50'
                      }`}
                    >
                      <FlaskConical className={`w-8 h-8 mb-2 ${studyExistingTypes.includes('FEA') ? 'text-slate-300' : 'text-teal-600'}`} />
                      <span className={`font-semibold ${studyExistingTypes.includes('FEA') ? 'text-slate-400' : 'text-slate-900'}`}>Feasibility Study</span>
                      <span className="text-xs text-slate-500 mt-1">FEA Request</span>
                      {studyExistingTypes.includes('FEA') && <Badge className="mt-2 bg-emerald-100 text-emerald-700 border border-emerald-300 text-[10px]" data-testid="fea-already-created-badge">Already created</Badge>}
                    </Label>
                  </div>
                  )}
                  {isCompanyRep() && (
                  <div className="relative">
                    <RadioGroupItem value="CTA" id="cta" className="peer sr-only" disabled={studyExistingTypes.includes('CTA')} />
                    <Label
                      htmlFor="cta"
                      className={`flex flex-col items-center justify-center rounded-lg border-2 p-6 transition-colors ${
                        studyExistingTypes.includes('CTA')
                          ? 'border-slate-200 bg-slate-50 opacity-50 cursor-not-allowed'
                          : 'border-slate-200 bg-white cursor-pointer hover:bg-slate-50 peer-data-[state=checked]:border-violet-500 peer-data-[state=checked]:bg-violet-50'
                      }`}
                    >
                      <FileCheck className={`w-8 h-8 mb-2 ${studyExistingTypes.includes('CTA') ? 'text-slate-300' : 'text-violet-600'}`} />
                      <span className={`font-semibold ${studyExistingTypes.includes('CTA') ? 'text-slate-400' : 'text-slate-900'}`}>CTA Request</span>
                      <span className="text-xs text-slate-500 mt-1">Clinical Trial Agreement</span>
                      {studyExistingTypes.includes('CTA') && <Badge className="mt-2 bg-emerald-100 text-emerald-700 border border-emerald-300 text-[10px]" data-testid="cta-already-created-badge">Already created</Badge>}
                    </Label>
                  </div>
                  )}
                  {/* iter165i — IRB Submission tile, placed next to CTA per
                      request. Disabled for now (workflow not yet built); the
                      tile shows a "Coming soon" badge and is fully inert
                      (radio item disabled, label cursor not-allowed). */}
                  {isCompanyRep() && (
                  <div className="relative" data-testid="new-request-type-irb-wrap">
                    <RadioGroupItem value="IRB" id="irb" className="peer sr-only" disabled />
                    <Label
                      htmlFor="irb"
                      aria-disabled="true"
                      data-testid="new-request-type-irb"
                      className="flex flex-col items-center justify-center rounded-lg border-2 p-6 transition-colors border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed"
                      onClick={(e) => e.preventDefault()}
                    >
                      <ShieldCheck className="w-8 h-8 mb-2 text-slate-400" />
                      <span className="font-semibold text-slate-500">IRB Submission</span>
                      <span className="text-xs text-slate-500 mt-1">Ethics Review</span>
                      <Badge className="mt-2 bg-amber-100 text-amber-700 border border-amber-300 text-[10px]" data-testid="irb-coming-soon-badge">Coming soon</Badge>
                    </Label>
                  </div>
                  )}
                  {isCoordinator() && (
                  <div className="relative">
                    <RadioGroupItem value="FT" id="ft" className="peer sr-only" />
                    <Label
                      htmlFor="ft"
                      data-testid="new-request-type-ft"
                      className="flex flex-col items-center justify-center rounded-lg border-2 p-6 transition-colors border-slate-200 bg-white cursor-pointer hover:bg-slate-50 peer-data-[state=checked]:border-emerald-500 peer-data-[state=checked]:bg-emerald-50"
                    >
                      <FileText className="w-8 h-8 mb-2 text-emerald-600" />
                      <span className="font-semibold text-slate-900">Financial Transaction</span>
                      <span className="text-xs text-slate-500 mt-1">FT Request</span>
                    </Label>
                  </div>
                  )}
                </RadioGroup>
              </div>
              )}

              {/* Loading indicator when switching types */}
              {formConfigLoading && (
                <div className="flex items-center justify-center py-6">
                  <BrandLoader size="sm" label="Loading form configuration…" />
                </div>
              )}

              {/* Form fields - only show when not loading */}
              {!formConfigLoading && (
                <>
              {getOrderedFormFields()}

              {/* Actions */}
              <div className="flex items-center justify-end gap-4 pt-4 border-t">
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => navigate(isCoordinatorOnly ? '/dashboard' : '/requests')}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className={isCoordinatorOnly ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-blue-900 hover:bg-blue-950'}
                  disabled={loading || formConfigLoading || (formData.type !== 'FT' && (formData.specialty_ids?.length === 0))}
                  data-testid="create-request-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (isCoordinatorOnly ? 'Create Financial Transaction' : 'Create Request')}
                </Button>
              </div>
              </>
              )}
            </form>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
